import type { Melody, NoteName } from '@tunasong/schemas'
import { chordTones, type ChordTone } from '../chord/chord-tones.js'
import type { VocalRange } from './types.js'

/**
 * Lines a singer can practise on a song with chords but no written melody (Pitch Trainer Phase 2,
 * plan M2; Bowie §1). They are sung back like a melody, so each comes as a `Melody`.
 */

/** One sung note of a chord line: which chord it belongs to, and which of its tones it is */
export interface ChordLineTone {
  midi: number
  name: NoteName
  /** The chord symbol as written */
  chord: string
  /** 1–7 above the chord's root */
  degree: number
  /**
   * Every chord this tone sounds over, in order, with its degree on each: set by `mergeChordLine`. A common tone held
   * across a change is one note on several chords, e.g. F, the 3rd of Dm7 and then the 7th of G7 (Bowie, #2440).
   */
  spans?: { chord: string; degree: number }[]
}

export interface ChordLine {
  tones: ChordLineTone[]
  /** Even notes, a beat each at 100 bpm: 600 ms */
  melody: Melody
}

const flatNames: NoteName[] = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B']

/**
 * The name a singer reads for a sung chord tone: never a double flat, so Cdim7's 7th and Gbm's 3rd read
 * A. Only the name: the degree and pitch stay the chord's (Bowie, on Glenn's M2 net).
 */
export const sungName = (tone: ChordTone): NoteName =>
  tone.name.endsWith('bb') ? flatNames[tone.pitchClass] : tone.name

/** 600 ms a beat */
export const chordLineBpm = 100

/** Ties go to the 3rd, then a suspended note, then the 7th, then the root */
const preference = [3, 4, 2, 7, 1]

const byPreference = (tones: ChordTone[]) =>
  [...tones].sort((a, b) => preference.indexOf(a.degree) - preference.indexOf(b.degree))

/**
 * What a chord's guide tone may be, in order of preference:
 * - a 7th chord its 3rd or 7th, a triad its 3rd or root;
 * - a sus chord its suspended note, the 4th if it has one, otherwise the 2nd, plus its 7th if it has one.
 *   A 9th is not a suspension, so C9sus4 sings F or Bb, never D (Bowie, M2 pre-review);
 * - anything else, such as a power chord, its root.
 */
const allowedTones = (tones: ChordTone[]) => {
  const of = (...degrees: number[]) => tones.filter(tone => degrees.includes(tone.degree))
  if (of(3).length > 0) {
    return byPreference(of(7).length > 0 ? of(3, 7) : of(3, 1))
  }
  const suspended = of(4).length > 0 ? of(4) : of(2)
  return byPreference(suspended.length > 0 ? [...suspended, ...of(7)] : of(1))
}

/** The tones a chord's guide tone may be, in order of preference; none for N.C. or an unknown symbol */
export const guideToneOptions = (symbol: string): ChordTone[] => allowedTones(chordTones(symbol) ?? [])

/** Every MIDI note of these tones inside the range; or, if none fits, within an octave either side of it */
const candidates = (tones: ChordTone[], range: VocalRange) => {
  const inside = (low: number, high: number) =>
    tones.flatMap(tone => {
      const midis: number[] = []
      for (let midi = Math.ceil(low); midi <= Math.floor(high); midi++) {
        if (((midi % 12) + 12) % 12 === tone.pitchClass) {
          midis.push(midi)
        }
      }
      return midis.map(midi => ({ midi, tone }))
    })
  const fitting = inside(range.low, range.high)
  return fitting.length > 0 ? fitting : inside(range.low - 12, range.high + 12)
}

/**
 * The candidate nearest `target`. Ties go by `preference`; the same tone in two octaves goes to the one
 * nearer the middle of the range, and only then to the lower (Bowie, M2 pre-review).
 */
const nearest = (options: ReturnType<typeof candidates>, target: number, centre: number) =>
  options.reduce<(typeof options)[number] | undefined>((best, option) => {
    if (!best) {
      return option
    }
    const distance = Math.abs(option.midi - target) - Math.abs(best.midi - target)
    if (distance !== 0) {
      return distance < 0 ? option : best
    }
    const rank = preference.indexOf(option.tone.degree) - preference.indexOf(best.tone.degree)
    if (rank !== 0) {
      return rank < 0 ? option : best
    }
    const offCentre = Math.abs(option.midi - centre) - Math.abs(best.midi - centre)
    if (offCentre !== 0) {
      return offCentre < 0 ? option : best
    }
    return option.midi < best.midi ? option : best
  }, undefined)

export const chordLineMelody = (midis: { midi: number; duration: number }[]): Melody => {
  let onset = 0
  return {
    type: 'melody',
    tempo: { bpm: chordLineBpm, meter: { top: 4, bottom: 4 } },
    grid: '8',
    notes: midis.map(({ midi, duration }) => {
      const note = { midi, onset, duration, velocity: 0.8, offsetMs: 0 }
      onset += duration
      return note
    }),
  }
}

/**
 * A chord line as it is sung: a tone repeated on consecutive chords is one note held across the change, a beat per
 * chord. The held common tone is the voice leading itself (Bowie, #2440), and the line is scored against these
 * merged notes, as a melody phrase is against its merged events. A merged tone keeps its first chord.
 */
export const mergeChordLine = (tones: ChordLineTone[]): ChordLine => {
  const merged = tones.reduce<{ tone: ChordLineTone; duration: number }[]>((notes, tone) => {
    const last = notes[notes.length - 1]
    const span = { chord: tone.chord, degree: tone.degree }
    return last && last.tone.midi === tone.midi
      ? [
          ...notes.slice(0, -1),
          { tone: { ...last.tone, spans: [...(last.tone.spans ?? []), span] }, duration: last.duration + 1 },
        ]
      : [...notes, { tone: { ...tone, spans: [span] }, duration: 1 }]
  }, [])
  return {
    tones: merged.map(({ tone }) => tone),
    melody: chordLineMelody(merged.map(({ tone, duration }) => ({ midi: tone.midi, duration }))),
  }
}

/** How many notes a line sings once a repeated pitch is merged into one held note */
export const distinctNotes = (tones: { midi: number }[]) =>
  tones.filter((tone, index) => index === 0 || tone.midi !== tones[index - 1].midi).length

/**
 * One guide tone per chord change, moving to the nearest allowed tone inside the range; ties go to the
 * 3rd. N.C. and unknown symbols are skipped, and a slash bass is never sung.
 *
 * Two lines are built, one starting on the first chord's 3rd and one on its other allowed tone (the 7th,
 * or the root of a triad), each nearest the middle of the range. The one that sings more distinct notes
 * is kept, so a vamp such as Am7 D7 moves G F# G F# rather than sitting on C; on a tie, the 3rd's
 * (Bowie, M2 pre-review).
 */
export const guideToneLine = (chords: string[], range: VocalRange): ChordLine => {
  const centre = (range.low + range.high) / 2
  const sung = chords.flatMap(chord => {
    const allowed = guideToneOptions(chord)
    return allowed.length > 0 ? [{ chord, allowed }] : []
  })
  const lineFrom = (start: ChordTone) =>
    sung.reduce<ChordLineTone[]>((line, { chord, allowed }) => {
      const previous = line[line.length - 1]
      const choice = previous
        ? nearest(candidates(allowed, range), previous.midi, centre)
        : nearest(candidates([start], range), centre, centre)
      return choice
        ? [...line, { midi: choice.midi, name: sungName(choice.tone), chord, degree: choice.tone.degree }]
        : line
    }, [])
  const starts = sung[0]?.allowed.slice(0, 2) ?? []
  const lines = starts.map(lineFrom)
  const tones = lines.reduce<ChordLineTone[]>(
    (best, line) => (distinctNotes(line) > distinctNotes(best) ? line : best),
    lines[0] ?? []
  )
  return mergeChordLine(tones)
}
